'use client'

import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { useErrorBoundaryCapture } from '@/lib/hooks/use-error-boundary-capture'
import { AlumniaLogo } from '@/components/alumnia-logo'
import { Button } from '@/components/ui/button'

/**
 * App-level Error Boundary — catches errors in all routes.
 * Renders inside the root layout so fonts/styles are available.
 * Stale-deploy self-heal + Sentry/PostHog capture live in the shared
 * `useErrorBoundaryCapture` hook (also used by the org-segment boundary).
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const t = useTranslations('errors')

  useErrorBoundaryCapture(error)

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="mx-auto w-full max-w-md text-center">
        <Link
          href="/"
          className="mb-10 inline-block rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <AlumniaLogo size={40} />
        </Link>

        <div className="mb-8 inline-flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 shadow-sm ring-1 ring-amber-200/60">
          <svg
            className="h-8 w-8 text-amber-600"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
            />
          </svg>
        </div>

        <h1 className="mb-2 text-2xl font-semibold text-gray-900">{t('somethingWentWrong')}</h1>
        <p className="mb-6 text-sm text-gray-600">{t('unexpectedIssue')}</p>

        {error.digest && (
          <p className="mb-4 text-xs text-gray-500">{t('errorId', { id: error.digest })}</p>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button size="lg" onClick={reset}>
            {t('tryAgain')}
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link href="/">{t('goHome')}</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
